import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useBreak, useHoursCurrent, usePunchIn, usePunchOut } from "../api/hooks";
import { errorText, useFlash } from "./Flash";
import { hm, timeOf } from "../lib/format";

/** Top-bar punch in / punch out, with a running timer while punched in, and break in / break out.
 * Breaks come from the break buttons (or the Hours page), so punching out asks nothing. Punch in stays
 * after punching out, in case you're called back: it reopens today's shift. */
export function PunchClock() {
  const q = useHoursCurrent();
  const punchIn = usePunchIn();
  const punchOut = usePunchOut();
  const brkMut = useBreak();
  const flash = useFlash();
  const [, tick] = useState(0);
  const open = q.data?.open;

  // Re-render every 30 seconds while punched in, so the timer moves.
  useEffect(() => {
    if (!open) return;
    const t = window.setInterval(() => tick((x) => x + 1), 30_000);
    return () => window.clearInterval(t);
  }, [open]);

  if (q.isPending || q.error || !q.data) return null;
  const today = q.data.today;

  if (open) {
    const toggleBreak = (to: "start" | "end") =>
      brkMut.mutate(to, {
        onSuccess: (s) =>
          flash(
            to === "start" ? `On a break from ${timeOf(s.breaks.at(-1)?.start ?? s.start)}.` : `Break over.`,
          ),
        onError: (err) => flash(errorText(err), "error"),
      });

    if (open.on_break) {
      return (
        <div className="punch is-in on-break">
          <span className="punch-status" title={`Punched in at ${timeOf(open.start)}`}>
            <i className="punch-dot is-paused" aria-hidden="true" />
            <span className="punch-since">
              On a break since {timeOf(open.breaks.at(-1)?.start ?? open.start)} ·{" "}
            </span>
            <b>{hm(open.worked_minutes)}</b>
          </span>
          <button
            type="button"
            className="punch-btn"
            disabled={brkMut.isPending}
            onClick={() => toggleBreak("end")}
          >
            End break
          </button>
        </div>
      );
    }

    // Minutes worked when the server answered, plus the time since.
    const minutes = open.worked_minutes + Math.floor((Date.now() - q.dataUpdatedAt) / 60_000);
    return (
      <div className="punch is-in">
        <span className="punch-status" title={`Punched in at ${timeOf(open.start)}`}>
          <i className="punch-dot" aria-hidden="true" />
          <span className="punch-since">In since {timeOf(open.start)} · </span>
          <b>{hm(minutes)}</b>
        </span>
        <button
          type="button"
          className="punch-btn quiet"
          disabled={brkMut.isPending}
          onClick={() => toggleBreak("start")}
        >
          Break
        </button>
        <button
          type="button"
          className="punch-btn"
          disabled={punchOut.isPending}
          onClick={() =>
            punchOut.mutate(undefined, {
              onSuccess: (s) => flash(`Punched out: ${hm(s.worked_minutes)} worked today.`),
              onError: (err) => flash(errorText(err), "error"),
            })
          }
        >
          Punch out
        </button>
      </div>
    );
  }

  // Punch in: after punching out today, it reopens today's shift (the time away becomes a break).
  const punchInButton = (
    <button
      type="button"
      className="punch-btn"
      disabled={punchIn.isPending}
      onClick={() =>
        punchIn.mutate(undefined, {
          onSuccess: (s) =>
            flash(
              today
                ? "Punched back in. The time since you punched out is a break."
                : `Punched in at ${timeOf(s.start)}.`,
            ),
          onError: (err) => flash(errorText(err), "error"),
        })
      }
    >
      Punch in
    </button>
  );
  if (!today) return punchInButton;
  return (
    <div className="punch">
      <Link
        className="punch is-done"
        to={`/year/${today.date.slice(0, 4)}/hours`}
        title="See or change today's shift"
      >
        Today {hm(today.worked_minutes)}
      </Link>
      {punchInButton}
    </div>
  );
}
